// frontend/src/components/InstructionNotificationBell.js
// ヘッダーに置く通知ベル（統合版）
//
// 統合対象：
//   - instruction_notifications: 指導症例通知（自分が trainer の pending 通知）
//   - staff_notifications:       汎用通知（配置確定 など）
//
// 仕様：
//   - 未読件数（両者合算）をバッジで表示
//   - クリックで一覧展開（最新順にマージ）
//   - 指導通知をクリック → InstructionNotificationModal
//   - 汎用通知をクリック → 既読化のみ
//   - 60 秒ごとに自動再取得

import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell } from 'lucide-react';
import { supabase } from '../supabase';
import { useOrganization } from '../contexts/OrganizationContext';
import InstructionNotificationModal from './InstructionNotificationModal';

const POLL_INTERVAL_MS = 60 * 1000;

export default function InstructionNotificationBell() {
  const { currentOrganization } = useOrganization();
  const [instructionNotifs, setInstructionNotifs] = useState([]);
  const [staffNotifs, setStaffNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [staffId, setStaffId] = useState(null);
  const [traineeNamesById, setTraineeNamesById] = useState({});
  const popupRef = useRef(null);

  // 自分の staff.id を localStorage から取得（login レスポンスに含まれる）
  useEffect(() => {
    const raw = localStorage.getItem('supabase_user');
    if (!raw) return;
    try {
      const u = JSON.parse(raw);
      if (u?.id) setStaffId(u.id);
    } catch (_) {
      // ignore
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    if (!currentOrganization?.id || !staffId) return;

    // 指導症例通知（自分が trainer の pending）
    const { data: instData, error: instError } = await supabase
      .from('instruction_notifications')
      .select('*')
      .eq('organization_id', currentOrganization.id)
      .eq('trainer_staff_id', staffId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
      .limit(50);
    if (instError) {
      console.error('指導通知の取得に失敗:', instError);
    } else {
      setInstructionNotifs(instData || []);
    }

    // 汎用通知（未読のみ）
    const { data: staffData, error: staffError } = await supabase
      .from('staff_notifications')
      .select('*')
      .eq('organization_id', currentOrganization.id)
      .eq('recipient_staff_id', staffId)
      .is('read_at', null)
      .order('created_at', { ascending: false })
      .limit(50);
    if (staffError) {
      console.error('汎用通知の取得に失敗:', staffError);
    } else {
      setStaffNotifs(staffData || []);
    }

    // 育成対象の名前をまとめて取得（表示用）
    const traineeIds = Array.from(
      new Set((instData || []).map((n) => n.trainee_staff_id))
    ).filter(Boolean);
    if (traineeIds.length > 0) {
      const { data: traineeRows } = await supabase
        .from('staff')
        .select('id, last_name, first_name')
        .in('id', traineeIds);
      const map = {};
      (traineeRows || []).forEach((s) => {
        map[s.id] = `${s.last_name || ''} ${s.first_name || ''}`.trim() || s.id;
      });
      setTraineeNamesById(map);
    }
  }, [currentOrganization?.id, staffId]);

  // 汎用通知を既読化
  const markStaffNotifRead = async (notifId) => {
    try {
      const { error } = await supabase
        .from('staff_notifications')
        .update({ read_at: new Date().toISOString() })
        .eq('id', notifId)
        .eq('organization_id', currentOrganization.id);
      if (error) throw error;
      setStaffNotifs((prev) => prev.filter((n) => n.id !== notifId));
    } catch (e) {
      console.error('❌ 既読化失敗:', e);
    }
  };

  useEffect(() => {
    loadNotifications();
    const t = setInterval(loadNotifications, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [loadNotifications]);

  // 外側クリックで閉じる
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // 2 系統をマージしたタイムライン（最新順）
  const merged = [
    ...instructionNotifs.map((n) => ({ kind: 'instruction', n, ts: n.created_at })),
    ...staffNotifs.map((n) => ({ kind: 'staff', n, ts: n.created_at })),
  ].sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));

  const unread = merged.length;

  if (!staffId) return null;

  return (
    <div style={{ position: 'relative' }} ref={popupRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        title="お知らせ"
        style={styles.bellButton}
      >
        <Bell size={20} />
        {unread > 0 && (
          <span style={styles.badge}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div style={styles.popup}>
          <div style={styles.popupHeader}>
            <span style={styles.popupTitle}>お知らせ</span>
            <button onClick={loadNotifications} style={styles.reloadBtn} title="再取得">↻</button>
          </div>
          {merged.length === 0 ? (
            <div style={styles.empty}>新しい通知はありません</div>
          ) : (
            <div style={styles.list}>
              {merged.map(({ kind, n }) => {
                if (kind === 'instruction') {
                  const traineeName =
                    traineeNamesById[n.trainee_staff_id]
                    || `(社員番号 ${n.trainee_employee_id || '不明'})`;
                  const roleLabel = n.role === 'scrub' ? '器械出し' : '外回り';
                  const procCount = Array.isArray(n.procedures) ? n.procedures.length : 0;
                  return (
                    <button
                      key={`inst-${n.id}`}
                      onClick={() => { setSelected(n); setOpen(false); }}
                      style={styles.item}
                    >
                      <div style={styles.itemMain}>
                        <div style={styles.itemDate}>
                          <span style={styles.kindTagInstruction}>指導</span>
                          {n.surgery_date}
                        </div>
                        <div style={styles.itemTitle}>
                          {n.surgery_procedure_name || '手術'}
                        </div>
                        <div style={styles.itemSub}>
                          <span style={styles.itemTag}>{roleLabel}</span>
                          <span>育成対象：{traineeName}</span>
                        </div>
                        <div style={styles.itemMeta}>対象術式 {procCount} 件</div>
                      </div>
                    </button>
                  );
                }
                return (
                  <button
                    key={`staff-${n.id}`}
                    onClick={() => markStaffNotifRead(n.id)}
                    style={styles.item}
                  >
                    <div style={styles.itemMain}>
                      <div style={styles.itemDate}>
                        <span style={styles.kindTagAssignment}>配置</span>
                        {n.surgery_date || ''}
                      </div>
                      <div style={styles.itemTitle}>{n.title}</div>
                      {n.body && <div style={styles.itemMeta}>{n.body}</div>}
                      <div style={styles.itemMeta}>クリックで既読にする</div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {selected && (
        <InstructionNotificationModal
          notification={selected}
          traineeName={traineeNamesById[selected.trainee_staff_id]}
          onClose={() => { setSelected(null); loadNotifications(); }}
        />
      )}
    </div>
  );
}

const styles = {
  bellButton: {
    position: 'relative',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: '8px',
    color: '#374151',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    position: 'absolute',
    top: 2,
    right: 2,
    background: '#ef4444',
    color: '#fff',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    padding: '1px 5px',
    borderRadius: '9px',
    minWidth: '16px',
    textAlign: 'center',
    lineHeight: '14px',
  },
  popup: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 8,
    width: 'min(360px, 90vw)',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 12,
    boxShadow: '0 12px 32px rgba(0,0,0,0.15)',
    zIndex: 10001,
    maxHeight: '70vh',
    display: 'flex',
    flexDirection: 'column',
  },
  popupHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    borderBottom: '1px solid #f3f4f6',
  },
  popupTitle: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
  reloadBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: '1rem',
    color: '#6b7280',
  },
  empty: {
    padding: '2rem 1rem',
    textAlign: 'center',
    color: '#9ca3af',
    fontSize: '0.9rem',
  },
  list: {
    overflowY: 'auto',
    flex: 1,
  },
  item: {
    width: '100%',
    background: '#fff',
    border: 'none',
    borderBottom: '1px solid #f3f4f6',
    padding: '12px 16px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  itemMain: { display: 'flex', flexDirection: 'column', gap: 4 },
  itemDate: { fontSize: '0.75rem', color: '#9ca3af' },
  itemTitle: { fontSize: '0.95rem', fontWeight: 600, color: '#111827' },
  itemSub: { display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', color: '#4b5563' },
  itemTag: {
    fontSize: '0.7rem',
    background: '#e0e7ff',
    color: '#3730a3',
    padding: '2px 8px',
    borderRadius: 999,
    fontWeight: 600,
  },
  itemMeta: { fontSize: '0.75rem', color: '#6b7280' },
  kindTagInstruction: {
    display: 'inline-block',
    marginRight: 6,
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: '0.7rem',
    fontWeight: 700,
    background: '#fce4ec',
    color: '#ad1457',
  },
  kindTagAssignment: {
    display: 'inline-block',
    marginRight: 6,
    padding: '1px 6px',
    borderRadius: 4,
    fontSize: '0.7rem',
    fontWeight: 700,
    background: '#fff3e0',
    color: '#e65100',
  },
};
